import type {Path} from 'three'
import {Vector2, BufferGeometry, Float32BufferAttribute} from 'three'

const dimensions = new Vector2(1600, 1000)

class CustomLineGeometry extends BufferGeometry {
  isClosed: boolean = false
  override type: string = 'CustomLineGeometry'
  path: Path
  pathDist: number
  lIndex: number
  parameters: {
    width: number
    height: number
    widthSegments: number
    heightSegments: number
  }
  constructor(heightSegments = 1, path: Path, pathDist: number, lIndex: number, isClosed: boolean) {
    const widthSegments = 1
    const width = 1
    const height = 1

    super()
    this.isClosed = isClosed

    this.path = path
    this.pathDist = pathDist

    this.lIndex = lIndex

    this.parameters = {
      width,
      height,
      widthSegments,
      heightSegments,
    }

    const width_half = width / 2
    const height_half = height / 2

    const gridX = Math.floor(widthSegments)
    const gridY = Math.floor(heightSegments)

    const gridX1 = gridX + 1
    const gridY1 = gridY + 1

    const segment_width = width / gridX
    const segment_height = height / gridY

    const indices = []
    const vertices = [] // svg path
    const vertices_previous = [] // svg path
    const vertices_next = [] // svg path
    const tangentScales = []

    const linePositions = []
    const vertexIndicies = []

    for (let iy = 0; iy < gridY1; iy++) {
      const y = iy * segment_height - height_half
      let t = 0
      let lineIndex = 0
      lineIndex = iy / (gridY1 - 1)

      if (this.pathDist > 0) {
        t = lineIndex
      } else {
        t = 1.0 - lineIndex
      }

      const t0 = this.restrictT(t - 1 / gridY1)
      const t1 = this.restrictT(t)
      const t2 = this.restrictT(t + 1 / gridY1)

      const vertex_0 = this.getPoint(t0)
      const vertex_1 = this.getPoint(t1)
      const vertex_2 = this.getPoint(t2)

      const tangentScale = [1, 1]

      if (!this.isClosed) {
        tangentScale[0] = t0 < 0 ? 0 : 1
        tangentScale[1] = t2 > 1 ? 0 : 1
      }

      for (let ix = 0; ix < gridX1; ix++) {
        const x = ix * segment_width - width_half

        linePositions.push(x, -y, 0)
        vertices.push(vertex_1.x, vertex_1.y, 0)
        vertices_previous.push(vertex_0.x, vertex_0.y, 0)
        vertices_next.push(vertex_2.x, vertex_2.y, 0)
        vertexIndicies.push(lineIndex, lIndex)

        if (tangentScale[0] === 0 || tangentScale[0] === 1) {
          tangentScales.push(tangentScale[0])
        }

        if (tangentScale[1] === 0 || tangentScale[1] === 1) {
          tangentScales.push(tangentScale[1])
        }
      }
    }

    for (let iy = 0; iy < gridY; iy++) {
      for (let ix = 0; ix < gridX; ix++) {
        const a = ix + gridX1 * iy
        const b = ix + gridX1 * (iy + 1)
        const c = ix + 1 + gridX1 * (iy + 1)
        const d = ix + 1 + gridX1 * iy

        indices.push(a, b, d)
        indices.push(b, c, d)
      }
    }

    this.setIndex(indices)
    this.setAttribute('position', new Float32BufferAttribute(vertices, 3))
    this.setAttribute('alineposition', new Float32BufferAttribute(linePositions, 3))
    this.setAttribute('aindex', new Float32BufferAttribute(vertexIndicies, 2))
    this.setAttribute('atangentscale', new Float32BufferAttribute(tangentScales, 2))

    this.setAttribute('positionprevious', new Float32BufferAttribute(vertices_previous, 3))
    this.setAttribute('positionnext', new Float32BufferAttribute(vertices_next, 3))
  }

  restrictT(t: number) {
    if (this.isClosed) {
      t = t - Math.floor(t)
    }
    return t
  }

  getPoint(t: number) {
    t = Math.min(1, Math.max(0, t))
    const vec2 = this.path.getPoint(t)
    const vecX = vec2.x / dimensions.x - 0.5
    const vecY = -(vec2.y / dimensions.y - 0.5) * (dimensions.y / dimensions.x)
    return {
      x: vecX,
      y: vecY,
    }
  }
}

const getRoundPoint = (x: number, y: number) => {
  return {
    x: x / dimensions.x - 0.5,
    y: -(y / dimensions.y - 0.5) * (dimensions.y / dimensions.x),
  }
}

export {CustomLineGeometry, getRoundPoint}
